import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from '../../../axios';
import { useNavigate } from 'react-router-dom';
import './RegisterForm.scss';
import logo from '../../../img/nekrodruk-logo-dark.png';
import hand from '../../../img/waving-hand-svgrepo-com.svg';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Collapse,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
} from '@mui/material';
import PasswordTextField from '../../PasswordTextField';

export default function RegisterForm() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/personForm');
    }
  }, [navigate]);

  const initialValues = {
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    address: '',
    phone: '',
    promoCode: '',
    acceptedTermsAndPrivacy: false,
  };

  const validationSchema = Yup.object({
    email: Yup.string().email('Niepoprawny format email').required('Email jest wymagany'),
    password: Yup.string()
      .required('Hasło jest wymagane')
      .min(6, 'Hasło musi mieć co najmniej 6 znaków')
      .max(20, 'Hasło może mieć maksymalnie 20 znaków')
      .matches(
        /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
        'Hasło musi zawierać co najmniej jedną cyfrę i jeden znak specjalny'
      ),
    confirmPassword: Yup.string()
      .required('Powtórzenie hasła jest wymagane')
      .oneOf([Yup.ref('password'), null], 'Hasła muszą być takie same'),
    name: Yup.string(),
    address: Yup.string(),
    phone: Yup.string().matches(/^[0-9]+$/, 'Numer telefonu musi składać się tylko z cyfr'),
    acceptedTermsAndPrivacy: Yup.boolean().oneOf(
      [true],
      'Musisz zaakceptować regulamin serwisu i politykę prywatności'
    ),
  });

  const [showPromo, setShowPromo] = useState(false);
  const [promoValidation, setPromoValidation] = useState({ status: 'idle', message: '' });
  const [lastCheckedPromoCode, setLastCheckedPromoCode] = useState('');

  const handleRegister = async (values, { setSubmitting }) => {
    const { email, password, name, address, phone, promoCode, acceptedTermsAndPrivacy } = values;
    const trimmedPromoCode = promoCode?.trim();
    setError('');
    if (
      trimmedPromoCode &&
      (promoValidation.status !== 'valid' || lastCheckedPromoCode !== trimmedPromoCode)
    ) {
      setError(
        promoValidation.status === 'invalid'
          ? promoValidation.message || 'Kod promocyjny jest nieprawidłowy.'
          : 'Sprawdź kod promocyjny przed rejestracją.'
      );
      setSubmitting(false);
      return;
    }
    try {
      await axios.post('/registerUser', {
        email,
        password,
        funeralhomename: name,
        funeralhomeaddress: address,
        tel: phone,
        promoCode: trimmedPromoCode || undefined,
        acceptedTerms: acceptedTermsAndPrivacy,
        acceptedPrivacy: acceptedTermsAndPrivacy,
      });
      setRegistered(true);
    } catch (error) {
      setError('Błąd podczas rejestracji. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if(registered) return (
    <Box className='RegisterForm'>
      <Paper elevation={0} className='formCard successCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={hand} alt='hand' className='hand_icon' />
          <Stack spacing={1} textAlign='center'>
            <Typography variant='h5' fontWeight={600}>Witaj!</Typography>
            <Typography variant='body1'>
              Udało ci się zarejestrować, teraz możesz przejść do logowania.
            </Typography>
          </Stack>
          <Button
            component={NavLink}
            to='/login'
            variant='contained'
            size='large'
            className='formSubmit'
          >
            Przejdź do logowania
          </Button>
        </Stack>
      </Paper>
    </Box>
  );

  return (
    <Box className='RegisterForm'>
      <Paper elevation={0} className='formCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={logo} alt='nekrodruk logo' className='logo' />
          <Typography component='h1' variant='h4' className='formTitle'>
            Rejestracja
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleRegister}
          >
            {({ isSubmitting }) => (
              <Form className='formContainer'>
                <Stack spacing={2} width='100%'>
                  <Field name='email'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='email'
                        label='Adres e-mail'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='password'>
                    {({ field, meta }) => (
                      <PasswordTextField
                        {...field}
                        label='Hasło'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='confirmPassword'>
                    {({ field, meta }) => (
                      <PasswordTextField
                        {...field}
                        label='Powtórz hasło'
                        variant='outlined'
                        fullWidth
                        required
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Typography variant='body2' className='register_info'>
                    Poniższe informacje są opcjonalne i służą wyłącznie do celów kontaktowych.
                  </Typography>
                  <Field name='name'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='text'
                        label='Nazwa zakładu'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='address'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='text'
                        label='Adres zakładu'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='phone'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='tel'
                        label='Telefon kontaktowy'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Button
                    type='button'
                    variant='text'
                    className='promoLink'
                    onClick={() => setShowPromo(!showPromo)}
                  >
                    {showPromo ? 'Ukryj pole kodu promocyjnego' : 'Masz kod promocyjny?'}
                  </Button>
                  <Collapse in={showPromo}>
                    <Field name='promoCode'>
                      {({ field, form }) => {
                        const handleChange = (event) => {
                          const { value } = event.target;
                          form.setFieldValue(field.name, value);

                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            setPromoValidation({ status: 'idle', message: '' });
                            setLastCheckedPromoCode('');
                            return;
                          }

                          setPromoValidation({ status: 'idle', message: '' });
                          setLastCheckedPromoCode('');
                        };

                        const handlePromoCheck = async () => {
                          const trimmedValue = field.value.trim();

                          if (!trimmedValue) {
                            setPromoValidation({ status: 'invalid', message: 'Kod nie może być pusty.' });
                            setLastCheckedPromoCode('');
                            return;
                          }

                          setPromoValidation({ status: 'checking', message: 'Sprawdzanie kodu...' });

                          try {
                            const response = await axios.post('/promocode/validate', { code: trimmedValue });
                            const successMessage = response.data?.message || 'Kod jest poprawny.';
                            setPromoValidation({ status: 'valid', message: successMessage });
                            setLastCheckedPromoCode(trimmedValue);
                          } catch (validationError) {
                            const message =
                              validationError?.response?.data?.message || 'Kod jest nieprawidłowy.';
                            setPromoValidation({ status: 'invalid', message });
                            setLastCheckedPromoCode('');
                          }
                        };

                        return (
                          <Stack spacing={0.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <TextField
                                {...field}
                                type='text'
                                label='Kod promocyjny'
                                variant='outlined'
                                fullWidth
                                value={field.value}
                                onChange={handleChange}
                                error={promoValidation.status === 'invalid'}
                              />
                              <Button
                                type='button'
                                variant='outlined'
                                onClick={handlePromoCheck}
                                disabled={promoValidation.status === 'checking'}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Sprawdź kod
                              </Button>
                            </Stack>
                            {promoValidation.status === 'checking' && (
                              <FormHelperText>Sprawdzanie kodu...</FormHelperText>
                            )}
                            {promoValidation.status === 'valid' && (
                              <FormHelperText sx={{ color: 'success.main' }}>
                                {promoValidation.message}
                              </FormHelperText>
                            )}
                            {promoValidation.status === 'invalid' && (
                              <FormHelperText error>{promoValidation.message}</FormHelperText>
                            )}
                          </Stack>
                        );
                      }}
                    </Field>
                  </Collapse>
                  <Stack spacing={1.5} alignItems='flex-start'>
                    <Field name='acceptedTermsAndPrivacy'>
                      {({ field, form, meta }) => (
                        <div className='consentField'>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color='primary'
                                checked={field.value}
                                name={field.name}
                                onChange={(event) => form.setFieldValue(field.name, event.target.checked)}
                                onBlur={() => form.setFieldTouched(field.name, true)}
                                inputProps={{ 'aria-required': true }}
                              />
                            }
                            label={
                              <Typography variant='body2'>
                                Akceptuję{' '}
                                <Link href="/Regulamin_nekrodruk.pdf" target='_blank' rel='noopener noreferrer'>
                                  regulamin serwisu
                                </Link>
                                {' '}oraz{' '}
                                <Link href="/Polityka_prywatnosci_nekrodruk.pdf" target='_blank' rel='noopener noreferrer'>
                                  politykę prywatności
                                </Link>
                                <Box component='span' className='requiredAsterisk' aria-hidden='true'>
                                  {' '}*
                                </Box>
                              </Typography>
                            }
                          />
                          {meta.touched && meta.error ? (
                            <FormHelperText error>{meta.error}</FormHelperText>
                          ) : null}
                        </div>
                      )}
                    </Field>
                  </Stack>
                  {error && (
                    <Alert severity='error' className='formAlert'>
                      {error}
                    </Alert>
                  )}
                  <Button
                    type='submit'
                    variant='contained'
                    size='large'
                    disabled={isSubmitting}
                    className='formSubmit'
                  >
                    zarejestruj się
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Paper>
    </Box>
  );
}
